import { type ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { BoxShadowSmall } from 'autogen/design-tokens/tokens';
import { ButtonWithIcon } from 'components/Button';
import { customScrollbar } from 'modules/helpers/mixins';

interface ScrollContainerProps {
    children: ReactNode;
    onBottomVisible?: (visible: boolean) => void;
    threshold?: number;
    rootMargin?: string;
    buttonOffset?: string;
}

const Wrapper = styled.div`
    position: relative;
    height: 100%;
`;

const Container = styled.div`
    position: relative; 
    ${customScrollbar};
    height: 100%;
`;

const Sentinel = styled.div`
    height: 1px;
`;

const ScrollToBottomButton = styled(ButtonWithIcon)<{ $visible: boolean }>`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    box-shadow: ${BoxShadowSmall};
    opacity: ${({ $visible: visible }) => (visible ? 1 : 0)};
    pointer-events: ${({ $visible: visible }) => (visible ? 'auto' : 'none')};
    transition: opacity 0.3s ease;
`;

const ObserverScrollContainer: React.FC<ScrollContainerProps> = ({
    children,
    onBottomVisible,
    threshold = 0.5,
    rootMargin = '100px',
    buttonOffset = '50px'
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        const sentinel = sentinelRef.current;

        if (!container || !sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const isAtBottom = entry.isIntersecting;
                onBottomVisible?.(isAtBottom);
                setShowScrollButton(!isAtBottom);
            },
            {
                root: container,
                threshold,
                rootMargin
            }
        );

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [onBottomVisible, threshold, rootMargin]);

    const handleScrollToBottom = () => {
        const container = containerRef.current;
        if (container) {
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
    };

    return (
        <Wrapper>
            <Container ref={containerRef}>
                {children}
                <Sentinel ref={sentinelRef} aria-hidden="true" />
            </Container>
            <ScrollToBottomButton
                style={{ bottom: buttonOffset }}
                variant="primaryInverted"
                icon="ArrowDown"
                size="md"
                $visible={showScrollButton}
                aria-label="Scroll to bottom"
                onClick={handleScrollToBottom}
            />
        </Wrapper>
    );
};

export default ObserverScrollContainer;
